
import { mapActions, mapGetters } from 'vuex';

/**
 * Component that tracks the scroll position and triggers a GTM event when the
 * scroll position reaches a certain threshold.
 *
 * Uses a throttled scroll listener with requestAnimationFrame for optimal performance.
 * The scroll listener is automatically removed once triggered and recreated when needed.
 *
 * Instanciated in the default layout
 */
export default {
  name: 'ScrollTracker',
  computed: {
    ...mapGetters('gtmEvents', ['scrollThreshold', 'hasTriggeredScroll']),
  },
  watch: {
    /**
     * If the scroll has been triggered, cleanup the scroll listener.
     * It will be recreated when we change page, that's handled in a gtm middleware
     */
    hasTriggeredScroll: {
      immediate: true,
      handler(hasTriggered) {
        if (hasTriggered) {
          this.cleanup();
        } else {
          this.setupScrollListener();
        }
      },
    },
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    ...mapActions('gtmEvents', ['setScrollTriggered']),
    /**
     * Setup the scroll listener.
     * Tried an approach with IntersectionObserver but it was not reliable for faster scrolling.
     * Tried to keep this as perf-friendly as possible.
     */
    setupScrollListener() {
      // Clean up any existing listener first
      this.cleanup();

      // Throttled scroll handler using requestAnimationFrame
      let ticking = false;

      this.scrollHandler = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            const scrolledPercentage =
              (window.scrollY / window.innerHeight) * 100;

            if (
              !this.hasTriggeredScroll &&
              scrolledPercentage >= this.scrollThreshold
            ) {
              this.setScrollTriggered();
            }

            ticking = false;
          });
          ticking = true;
        }
      };

      // passive: true tells the browser this listener will never call preventDefault()
      // allowing immediate scroll without waiting for JS execution
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    },

    cleanup() {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
    },
  },
};
